import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {Button} from 'primereact/button'
import {Checkbox} from 'primereact/checkbox'
import {Sidebar} from 'primereact/sidebar'
import React, {forwardRef, useEffect, useImperativeHandle} from 'react'
import {ReactComponent as DuoSnow} from '../../assets/icons/DuoSnow.svg'
import {ReactComponent as LockColumn} from '../../assets/icons/LockColumn.svg'
import {ReactComponent as LockRow} from '../../assets/icons/LockRow.svg'
import TranslateUtils from '../../i18n/TranslateUtils'
import {log} from '../../log/Logs'
import {useDatatableContext} from './DataTableContext'
import {
    getShouldRememberTableLock,
    getTableLock,
    getTableLockedColumns,
    getTableLockedRows,
    storeShouldRememberTableLock,
} from './StyledTableUtils'
import {
    getTableLockEventKey,
    onTableLockChangeListener,
    removeRefLockChangeListener,
    tableLockEvent,
    tableLockEventTypes,
} from './broadcast/TableLockEvent'

const defaultTableLock = {
    rowLock: false,
    columnLock: false,
}

const StyledLockDataTable = forwardRef((props, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    const {translateDatatable, translateKeys} = TranslateUtils()

    const {tableType} = useDatatableContext()

    const visible = useSignal(false)
    const tableLock = useSignal(defaultTableLock)
    const tableLockColumns = useSignal([])
    const tableLockRows = useSignal([])

    const shouldRememberLockDatatable = useSignal(false)

    const handleShowForm = () => {
        visible.value = true
        if (
            !tableLock.value.columnLock &&
            !tableLock.value.rowLock &&
            tableLockColumns.value.length === 0 &&
            tableLockRows.value.length === 0
        ) {
            const didRememberSettings = getShouldRememberTableLock(tableType)
            if (didRememberSettings) {
                const storedTableLock = getTableLock(tableType)
                if (
                    storedTableLock &&
                    Object.keys(storedTableLock).length > 0
                ) {
                    tableLock.value = {
                        ...tableLock.value,
                        ...storedTableLock,
                    }
                }

                const storedTableLockColumns = getTableLockedColumns(tableType)
                if (
                    storedTableLockColumns &&
                    storedTableLockColumns.length > 0
                ) {
                    tableLockColumns.value = storedTableLockColumns
                }

                const storedTableLockRows = getTableLockedRows(tableType)
                if (storedTableLockRows && storedTableLockRows.length > 0) {
                    tableLockRows.value = storedTableLockRows
                }
            } else {
                tableLock.value = defaultTableLock
                tableLockColumns.value = []
                tableLockRows.value = []
            }

            shouldRememberLockDatatable.value = didRememberSettings
        }
    }

    const handleHideForm = () => {
        visible.value = false
    }

    const handleCheckShouldRememberLockDatatable = checked => {
        shouldRememberLockDatatable.value = checked

        storeShouldRememberTableLock(tableType, checked)
    }

    const handleRestoreTableLock = () => {
        onTableLockChangeListener(
            tableType,
            tableLockEventTypes.TABLE_LOCK_RESTORE_DEFAULT,
        )

        tableLock.value = defaultTableLock
        tableLockColumns.value = []
        tableLockRows.value = []
        shouldRememberLockDatatable.value = false
    }

    const handleChangeTableLock = () => {
        onTableLockChangeListener(
            tableType,
            tableLockEventTypes.TABLE_LOCK_CHANGE,
            tableLock.value,
        )

        handleHideForm()
    }

    const handleDiscardColumnsLocked = () => {
        onTableLockChangeListener(
            tableType,
            tableLockEventTypes.TABLE_LOCK_DISCARD_COLUMNS,
        )

        tableLockColumns.value = []
    }

    const handleDiscardRowsLocked = () => {
        onTableLockChangeListener(
            tableType,
            tableLockEventTypes.TABLE_LOCK_DISCARD_ROWS,
        )

        tableLockRows.value = []
    }

    useEffect(() => {
        tableLockEvent.on(
            `${getTableLockEventKey(
                tableType,
                tableLockEventTypes.REF_LOCK_CHANGE_COLUMNS,
            )}`,
            (newSelectedItems = []) => {
                tableLockColumns.value = newSelectedItems
            },
        )

        tableLockEvent.on(
            `${getTableLockEventKey(
                tableType,
                tableLockEventTypes.REF_LOCK_CHANGE_ROWS,
            )}`,
            (newSelectedItems = []) => {
                tableLockRows.value = newSelectedItems
            },
        )

        return () => {
            removeRefLockChangeListener(
                tableType,
                tableLockEventTypes.REF_LOCK_CHANGE_COLUMNS,
            )

            removeRefLockChangeListener(
                tableType,
                tableLockEventTypes.REF_LOCK_CHANGE_ROWS,
            )
        }
    }, [])

    return (
        <Sidebar
            className="w-full md:w-25rem lg:w-30rem"
            visible={visible.value}
            onHide={handleHideForm}
            showCloseIcon={true}
            blockScroll
            position="right"
            baseZIndex={9999}>
            <div className="py-2 px-1">
                {log(tableLockColumns.value)}
                {/* Header */}
                <div className="flex align-items-center">
                    <div className="mr-2">
                        <DuoSnow style={{fontSize: '26px'}} />
                    </div>
                    <div>
                        <h3 className="font-bold m-0">Lock Columns or Rows</h3>
                    </div>
                </div>
                <div>
                    <p>
                        Freezing locks columns or rows to keep them visible
                        while scrolling through data.
                    </p>
                </div>
                <hr />
                {/* Content */}
                <div className="mb-5">
                    <div className="mb-3">
                        <div className="flex align-items-center justify-content-between mb-3">
                            <div>
                                <h5 className="mb-0 font-bold">
                                    <span className="mr-2">
                                        <LockColumn width={22} />
                                    </span>
                                    Lock Columns
                                </h5>
                            </div>
                            <div></div>
                        </div>
                    </div>
                    <div>
                        <div className="flex align-items-center justify-content-between mb-3">
                            <label htmlFor="lockColumns">
                                Enable Column Lock ?
                            </label>
                            <Checkbox
                                inputId="lockColumns"
                                checked={tableLock.value?.columnLock}
                                onChange={e => {
                                    tableLock.value = {
                                        ...tableLock.value,
                                        columnLock: e.checked,
                                    }
                                }}
                            />
                        </div>
                        {tableLockColumns.value?.length > 0 ? (
                            <React.Fragment>
                                <div className="mb-3">
                                    <p>
                                        <span className="font-bold text-darkcyan">
                                            Locked Columns:{' '}
                                            {tableLockColumns.value.length}
                                        </span>
                                    </p>
                                </div>
                                <div>
                                    <Button
                                        icon="pi pi-trash"
                                        className="p-button-danger p-button-text col-12"
                                        label="Discard Columns Locked"
                                        onClick={() => {
                                            handleDiscardColumnsLocked()
                                        }}
                                    />
                                </div>
                            </React.Fragment>
                        ) : null}
                    </div>
                </div>
                <hr />
                <div className="mb-5">
                    <div className="mb-3">
                        <div className="flex align-items-center justify-content-between mb-3">
                            <div>
                                <h5 className="mb-0 font-bold">
                                    <span className="mr-2">
                                        <LockRow width={22} />
                                    </span>
                                    Lock Rows
                                </h5>
                            </div>
                            <div></div>
                        </div>
                    </div>
                    <div>
                        <div className="flex align-items-center justify-content-between mb-3">
                            <label htmlFor="lockRows">Enable Row Lock ?</label>
                            <Checkbox
                                inputId="lockRows"
                                checked={tableLock.value?.rowLock}
                                onChange={e => {
                                    tableLock.value = {
                                        ...tableLock.value,
                                        rowLock: e.checked,
                                    }
                                }}
                            />
                        </div>

                        {tableLockRows.value?.length > 0 ? (
                            <React.Fragment>
                                <div className="mb-3">
                                    <p>
                                        <span className="font-bold text-darkcyan">
                                            Locked Rows:{' '}
                                            {tableLockRows.value.length}
                                        </span>
                                    </p>
                                </div>
                                <div>
                                    <Button
                                        icon="pi pi-trash"
                                        className="p-button-danger p-button-text col-12"
                                        label="Discard Rows Locked"
                                        onClick={() => {
                                            handleDiscardRowsLocked()
                                        }}
                                    />
                                </div>
                            </React.Fragment>
                        ) : null}
                    </div>
                </div>
                <hr />
                {/* Footer */}
                <div className="mb-5 pb-5">
                    <div className="flex align-items-center mb-3">
                        <div className="mr-2">
                            <Checkbox
                                checked={shouldRememberLockDatatable.value}
                                onChange={e => {
                                    handleCheckShouldRememberLockDatatable(
                                        e.checked,
                                    )
                                }}
                            />
                        </div>
                        <div>
                            <span>
                                Remember my locked columns and rows settings
                            </span>
                        </div>
                    </div>
                    <Button
                        label={translateDatatable(
                            'RESTORE TO DEFAULT',
                        ).toUpperCase()}
                        icon="pi pi-refresh"
                        className="col-12 p-button-lg p-button-outlined font-bold mb-2"
                        onClick={() => {
                            handleRestoreTableLock()
                        }}
                    />
                    <Button
                        label={translateDatatable('Submit').toUpperCase()}
                        icon="pi pi-send"
                        className="col-12 p-button-lg font-bold"
                        onClick={() => {
                            handleChangeTableLock()
                        }}
                    />
                </div>
            </div>
        </Sidebar>
    )
})

export default StyledLockDataTable
